import { esc, openModal, closeModal, onModalClose, showModalErr } from '../ui/modal';
import { t } from '../ui/lang';
import { mercadoStepsHtml } from './farm-mercado-setup';
const CVU_RE = /^\d{22}$/;
/**
 * Add / edit one Mercado API device: a Mercado Pago account given by its production Access
 * Token and the CVU transfers arrive on. The server checks the token against MP (GET
 * /users/me) and refuses test keys. On edit a blank key keeps the stored one. The ✕ is the
 * only dismissal: the actions row holds Delete + Save, so it is registered via onModalClose
 * (otherwise the ✕ would click the first `.modal__cancel`, which is Delete).
 */
export function showMercadoDeviceModal(farmId, existing, onDone) {
    const editing = existing !== null;
    const lazyChecked = editing ? existing.lazyPoll : true;
    const modal = openModal(`
        <h2 class="modal__title">${editing ? t('farmdev.mercado_edit_title', { name: esc(existing.label) }) : t('farmdev.mercado_add_title')}</h2>
        <div class="mpd-layout">
        <form class="modal__form" id="mpd-form" novalidate>
            <div class="modal__field">
                <label>${t('farmdev.mercado_label')}</label>
                <input id="mpd-label" type="text" autocomplete="off" maxlength="100" value="${editing ? esc(existing.label) : ''}"
                    placeholder="${editing ? '' : t('farmdev.mercado_label_ph')}" />
            </div>
            <div class="modal__field">
                <label>CVU</label>
                <input id="mpd-cvu" type="text" inputmode="numeric" autocomplete="off" maxlength="30" value="${editing ? esc(existing.cvu) : ''}"
                    placeholder="0000000000000000000000" />
                <p class="modal__hint">${t('farmdev.mercado_cvu_hint')}</p>
            </div>
            <div class="modal__field">
                <label>${t('farmdev.mercado_token')}</label>
                <input id="mpd-token" type="password" autocomplete="new-password" maxlength="300"
                    placeholder="${editing && existing.tokenHint ? esc(existing.tokenHint) : 'APP_USR-…'}" />
                <p class="modal__hint">${editing ? t('farmdev.mercado_token_keep') : t('farmdev.mercado_token_hint')}</p>
            </div>
            <div class="modal__field">
                <label class="settings-toggle">
                    <input id="mpd-lazy" type="checkbox"${lazyChecked ? ' checked' : ''} />
                    <span>${t('farmdev.mercado_lazy_label')}</span>
                </label>
                <p class="modal__hint">${t('farmdev.mercado_lazy_hint')}</p>
            </div>
            <div id="mpd-error" class="modal__error" style="display:none"></div>
        </form>
        <details class="clicker-config-help mpd-help"${editing ? '' : ' open'}>
            <summary class="clicker-config-help__title">${t('farmdev.mercado_howto_title')}</summary>
            <ol class="mpd-help__steps">${mercadoStepsHtml([1, 2, 3, 4, 5])}</ol>
        </details>
        </div>
        <div class="modal__actions mpd-actions">
            ${editing ? `<button type="button" class="modal__cancel" id="mpd-delete">${t('common.delete')}</button>` : ''}
            <button type="submit" form="mpd-form" class="modal__submit">${editing ? t('common.save') : t('common.create')}</button>
        </div>
    `);
    modal.classList.add('modal--wide');
    onModalClose(modal, closeModal);
    const errorEl = modal.querySelector('#mpd-error');
    const submitBtn = modal.querySelector('.modal__submit');
    modal.querySelector('#mpd-delete')?.addEventListener('click', async () => {
        if (!existing || !confirm(t('farmdev.mercado_confirm_delete', { name: existing.label })))
            return;
        try {
            const r = await fetch(`/api/farms/${farmId}/mercado-devices/${encodeURIComponent(existing.udid)}`, { method: 'DELETE' });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                showModalErr(errorEl, data.error ?? t('farmdev.mercado_save_failed'));
                return;
            }
            closeModal();
            onDone();
        }
        catch {
            showModalErr(errorEl, t('farmdev.network_error'));
        }
    });
    modal.querySelector('#mpd-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const label = modal.querySelector('#mpd-label').value.trim();
        const cvu = modal.querySelector('#mpd-cvu').value.replace(/\s+/g, '');
        const token = modal.querySelector('#mpd-token').value.trim();
        const lazyPoll = modal.querySelector('#mpd-lazy').checked;
        if (editing && !label) {
            showModalErr(errorEl, t('farmdev.mercado_label_required'));
            return;
        }
        if (!CVU_RE.test(cvu)) {
            showModalErr(errorEl, t('farmdev.mercado_cvu_invalid'));
            return;
        }
        if (!editing && !token) {
            showModalErr(errorEl, t('farmdev.mercado_token_required'));
            return;
        }
        const body = { label, cvu, lazy_poll: lazyPoll };
        if (token)
            body['api_token'] = token;
        submitBtn.disabled = true;
        try {
            const url = editing
                ? `/api/farms/${farmId}/mercado-devices/${encodeURIComponent(existing.udid)}`
                : `/api/farms/${farmId}/mercado-devices`;
            const r = await fetch(url, {
                method: editing ? 'PATCH' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                showModalErr(errorEl, data.error ?? t('farmdev.mercado_save_failed'));
                return;
            }
            closeModal();
            // Saved, but the running poller could not be restarted with the new spec.
            if (data.warning)
                alert(t('farmdev.mercado_restart_warning', { reason: data.warning }));
            onDone();
        }
        catch {
            showModalErr(errorEl, t('farmdev.network_error'));
        }
        finally {
            submitBtn.disabled = false;
        }
    });
}
